import { useState } from 'react';
import { Folder, FileCode, ChevronRight, Loader2, ArrowLeft, RefreshCw, Home, BrainCircuit, Edit3 } from 'lucide-react';
import { Button, Card } from '@shared/ui';
import { useGithubContents } from '@shared/api';

interface FileExplorerProps {
    repoFullName: string;
    branch: string;
    onFileSelect: (path: string) => void;
    onAnalyze?: (path: string) => void;
}

export function FileExplorer({ repoFullName, branch, onFileSelect, onAnalyze }: FileExplorerProps) {
    const [currentPath, setCurrentPath] = useState('');
    const { data: contents, isLoading, isError, refetch } = useGithubContents(repoFullName, currentPath, branch || 'main');
    
    const handleFolderClick = (path: string) => {
        setCurrentPath(path);
    };

    const navigateToBreadcrumb = (index: number) => {
        const parts = currentPath.split('/');
        const newPath = parts.slice(0, index + 1).join('/');
        setCurrentPath(newPath);
    };

    const goHome = () => setCurrentPath('');

    const isEmpty = !isLoading && (!contents || contents.length === 0);
    const pathParts = currentPath ? currentPath.split('/') : [];

    return (
        <Card padding="none" className="h-full flex flex-col border-border/50 bg-surface/30 overflow-hidden">
            {/* Header con Controles */}
            <div className="p-3 border-b border-border bg-surface-hover/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-primary/10 rounded-md">
                        <Folder className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-[12px] font-mono font-bold uppercase tracking-wider text-text-primary">Explorer</span>
                </div>
                <div className="flex gap-2">
                    {onAnalyze && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => onAnalyze(currentPath || "*")} 
                            className="h-11 w-11 p-0 text-primary hover:bg-primary/20 border border-primary/10"
                            title="Analizar esta carpeta con IA"
                        >
                            <BrainCircuit className="h-5.5 w-5.5" />
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-11 w-11 p-0 border border-border/50">
                        <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Breadcrumbs Interactivas (Rutas de carpetas) */}
            <div className="px-4 py-3 bg-background/50 border-b border-border flex items-center gap-1 overflow-x-auto no-scrollbar">
                <button 
                    onClick={goHome}
                    className={`p-2 rounded hover:bg-primary/10 transition-colors ${!currentPath ? 'text-primary' : 'text-text-muted'}`}
                >
                    <Home className="h-5 w-5" />
                </button>
                
                {pathParts.map((part, index) => (
                    <div key={index} className="flex items-center gap-1 flex-shrink-0">
                        <ChevronRight className="h-4 w-4 text-text-muted/50" />
                        <button
                            onClick={() => navigateToBreadcrumb(index)}
                            className={`px-2.5 py-1.5 rounded text-[12px] font-mono hover:bg-primary/10 transition-colors ${
                                index === pathParts.length - 1 ? 'text-primary font-bold' : 'text-text-secondary'
                            }`}
                        >
                            {part}
                        </button>
                    </div>
                ))}
            </div>

            {/* Lista de Archivos */}
            <div className="flex-1 overflow-y-auto min-h-[350px] p-1">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full py-20 gap-3">
                        <Loader2 className="h-6 w-6 animate-spin text-primary opacity-50" />
                        <span className="text-[10px] font-mono text-text-muted uppercase tracking-widest">Sincronizando...</span>
                    </div>
                ) : isError ? (
                    <div className="p-6 text-center space-y-4">
                        <div className="inline-flex p-3 bg-error/10 rounded-full">
                            <RefreshCw className="h-5 w-5 text-error" />
                        </div>
                        <p className="text-[10px] font-mono text-error uppercase font-bold">Error de Acceso</p>
                        <Button variant="primary" size="sm" onClick={() => refetch()} className="text-[9px] h-7 w-full">Reintentar</Button>
                    </div>
                ) : (
                    <div className="space-y-0.5">
                        {/* Botón para subir nivel si no estamos en root */}
                        {currentPath && (
                            <button
                                onClick={() => {
                                    const parts = currentPath.split('/');
                                    parts.pop();
                                    setCurrentPath(parts.join('/'));
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-xs font-mono text-text-muted hover:text-primary hover:bg-primary/5 rounded-md transition-all group"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                <span>..</span>
                            </button>
                        )}

                        {contents?.map((item: any) => (
                            <div key={item.path} className="group relative flex items-center">
                                <button
                                    type="button"
                                    onClick={() => item.type === 'dir' ? handleFolderClick(item.path) : onFileSelect(item.path)}
                                    className="flex-1 flex items-center gap-3 px-3 py-2 text-xs font-mono text-text-secondary hover:bg-primary/10 hover:text-primary rounded-md transition-all group border border-transparent hover:border-primary/20"
                                >
                                    {item.type === 'dir' ? (
                                        <div className="p-1 bg-primary/5 rounded group-hover:bg-primary/20">
                                            <Folder className="h-3.5 w-3.5 text-primary/70 group-hover:text-primary" />
                                        </div>
                                    ) : (
                                        <div className="p-1 bg-surface-active rounded group-hover:bg-primary/20">
                                            <FileCode className="h-3.5 w-3.5 text-text-muted group-hover:text-primary" />
                                        </div>
                                    )}
                                    <span className="truncate flex-1 text-left">{item.name}</span>
                                    {item.type === 'dir' && <ChevronRight className="h-3 w-3 opacity-30 group-hover:opacity-100" />}
                                </button>
                                
                                {onAnalyze && item.type === 'file' && (
                                    <div className="absolute right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all z-10">
                                        <button
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                const url = `/editor?projectId=${window.location.pathname.split('/').pop()}&filePath=${item.path}&type=github&branch=${branch}`;
                                                window.open(url, '_blank');
                                            }}
                                            className="p-1.5 rounded-md bg-surface-active text-text-primary hover:bg-primary hover:text-white border border-border"
                                            title="Abrir en Editor"
                                        >
                                            <Edit3 className="h-3 w-3" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onAnalyze(item.path); }}
                                            className="p-1.5 rounded-md bg-primary/20 text-primary hover:bg-primary hover:text-white transition-all"
                                            title="Analizar con IA"
                                        >
                                            <BrainCircuit className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        {isEmpty && (
                            <div className="flex flex-col items-center justify-center h-full py-16 text-center opacity-40">
                                <Folder className="h-10 w-10 text-text-muted mb-2 opacity-20" />
                                <p className="text-[10px] font-mono uppercase">Directorio Vacío</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer Informativo */}
            <div className="p-2 border-t border-border bg-surface-hover/30">
                <div className="flex items-center gap-2 text-[9px] font-mono text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                    <span>Branch: <strong>{branch || 'main'}</strong></span>
                </div>
            </div>
        </Card>
    );
}
